import { DEFAULT_FONT_ID } from '../fonts/catalog';

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type TextTransformMode = 'none' | 'upper' | 'lower' | 'smallcaps';
export type BaselineMode = 'straight' | 'arc' | 'wave';
export type TextRenderMode = 'fill' | 'stroke' | 'fillStroke' | 'mask';
export type AnchorX = 'left' | 'center' | 'right';
export type AnchorY = 'top' | 'middle' | 'bottom';

export interface BaselineSettings {
  mode: BaselineMode;
  /** Arc: circle radius in px. Positive bends upward, negative downward. |r| < 1 falls back to straight. */
  arcRadius: number;
  /** Arc: rotates the whole arc around its centre, in degrees. */
  arcOffset: number;
  /** Wave: peak amplitude in px. */
  waveAmplitude: number;
  /** Wave: cycles per 1000 px of baseline length. */
  waveFrequency: number;
  /** Wave: phase in degrees. */
  wavePhase: number;
  /** Rotate each glyph to follow the curve, instead of keeping it upright. */
  followTangent: boolean;
}

export interface TextSettings {
  text: string;
  fontId: string;

  fontSize: number;
  autoFit: boolean;
  /** Fraction of the canvas width the block should span when autoFit is on. */
  autoFitPercent: number;

  /** Applied to the `wght` axis when the font has one. */
  weight: number;
  /** All other variable axes, by tag. */
  variation: Record<string, number>;

  /** Letter spacing in em. */
  tracking: number;
  /** Extra word spacing in em, added on top of the space glyph's own advance. */
  wordSpacing: number;
  /** Line advance as a multiple of the font size. */
  lineHeight: number;
  align: TextAlign;

  anchorX: AnchorX;
  anchorY: AnchorY;
  /** Offset from the anchor, as a fraction of canvas size (resolution independent). */
  offsetX: number;
  offsetY: number;

  /** Whole-block rotation in degrees. */
  rotation: number;
  skewX: number;
  skewY: number;

  transform: TextTransformMode;
  /** Cap height ratio used to fake small caps when the font has no `smcp` feature. */
  smallCapsScale: number;

  baseline: BaselineSettings;

  /** Bézier flattening tolerance in output pixels. Lower = smoother contours, slower. */
  outlineTolerance: number;

  visible: boolean;
  renderMode: TextRenderMode;
  strokeWidth: number;

  /**
   * Grows (+) or shrinks (−) the *field* in pixels. Purely an SDF offset: it changes
   * where techniques consider the text to be, without altering the drawn glyphs.
   */
  dilate: number;
}

export const DEFAULT_TEXT_SETTINGS: TextSettings = {
  text: 'FORM',
  fontId: DEFAULT_FONT_ID,

  fontSize: 420,
  autoFit: true,
  autoFitPercent: 0.78,

  weight: 400,
  variation: {},

  tracking: 0.02,
  wordSpacing: 0,
  lineHeight: 1,
  align: 'center',

  anchorX: 'center',
  anchorY: 'middle',
  offsetX: 0,
  offsetY: 0,

  rotation: 0,
  skewX: 0,
  skewY: 0,

  transform: 'none',
  smallCapsScale: 0.76,

  baseline: {
    mode: 'straight',
    arcRadius: 900,
    arcOffset: 0,
    waveAmplitude: 40,
    waveFrequency: 2,
    wavePhase: 0,
    followTangent: true,
  },

  outlineTolerance: 0.25,

  visible: true,
  renderMode: 'fill',
  strokeWidth: 3,

  dilate: 0,
};

/**
 * Stable cache key for everything that changes the geometry of the text field.
 * Purely visual settings (visible, renderMode, strokeWidth) are excluded so that
 * toggling them does not trigger an SDF rebuild.
 */
export function textFieldCacheKey(s: TextSettings, width: number, height: number): string {
  const geometry = {
    t: s.text,
    f: s.fontId,
    fs: s.fontSize,
    af: s.autoFit,
    afp: s.autoFitPercent,
    w: s.weight,
    v: s.variation,
    tr: s.tracking,
    ws: s.wordSpacing,
    lh: s.lineHeight,
    al: s.align,
    ax: s.anchorX,
    ay: s.anchorY,
    ox: s.offsetX,
    oy: s.offsetY,
    r: s.rotation,
    kx: s.skewX,
    ky: s.skewY,
    tf: s.transform,
    sc: s.smallCapsScale,
    b: s.baseline,
    ot: s.outlineTolerance,
    d: s.dilate,
    W: width,
    H: height,
  };
  return JSON.stringify(geometry);
}
